import api, { handleResponse, handleError } from './api';

const wrap = async (call) => { try { return handleResponse(await call()); } catch (e) { return handleError(e); } };
const base = (ownerType, ownerId) => `/pages/${ownerType}/${ownerId}`;
const multipart = { headers: { 'Content-Type': 'multipart/form-data' } };

/**
 * A club's or Pro page's people, from its manage surface: the contact link + QR, contacts,
 * groups, the CSV / Excel importer, the SPECIFIC PEOPLE search and the page's own sends.
 * `ownerType` is 'club' or 'pro'. The public /join/{code} calls are at the bottom.
 */
const audienceService = {
  // ── The link ──
  getLink: (t, id) => wrap(() => api.get(`${base(t, id)}/contacts/link`)),
  rotateLink: (t, id) => wrap(() => api.post(`${base(t, id)}/contacts/link/rotate`)),
  setLinkHeadline: (t, id, headline) => wrap(() => api.put(`${base(t, id)}/contacts/link/headline`, { headline })),
  // What the join page insists on: { requireEmail, requirePhone }; both false = either address.
  setLinkRequirements: (t, id, req) => wrap(() => api.put(`${base(t, id)}/contacts/link/requirements`, req)),

  // ── Contacts ──
  /** { items, total, page, pageSize, counts: { contacts, withEmail, withPhone, claimed, followers, unsubscribed } } */
  listContacts: (t, id, params = {}) => wrap(() => api.get(`${base(t, id)}/contacts`, { params })),
  addContact: (t, id, payload) => wrap(() => api.post(`${base(t, id)}/contacts`, payload)),
  updateContact: (t, id, contactId, payload) => wrap(() => api.patch(`${base(t, id)}/contacts/${contactId}`, payload)),
  deleteContacts: (t, id, ids) => wrap(() => api.post(`${base(t, id)}/contacts/delete`, { ids })),

  // ── Groups ──
  listGroups: (t, id) => wrap(() => api.get(`${base(t, id)}/contacts/groups`)),
  createGroup: (t, id, name) => wrap(() => api.post(`${base(t, id)}/contacts/groups`, { name })),
  renameGroup: (t, id, groupId, name) => wrap(() => api.put(`${base(t, id)}/contacts/groups/${groupId}`, { name })),
  deleteGroup: (t, id, groupId) => wrap(() => api.delete(`${base(t, id)}/contacts/groups/${groupId}`)),
  /** { contactIds, userIds, remove } — userIds are followers / members who get a contact row made for them. */
  setGroupMembers: (t, id, groupId, payload) => wrap(() => api.post(`${base(t, id)}/contacts/groups/${groupId}/members`, payload)),

  // ── Import ──
  previewImport: (t, id, file) => {
    const form = new FormData();
    form.append('file', file);
    return wrap(() => api.post(`${base(t, id)}/contacts/import/preview`, form, multipart));
  },
  /** mapping: { email, phone, name, firstName, lastName, defaultDialCode, groupId, newGroupName, consent } (column indexes). */
  commitImport: (t, id, file, mapping) => {
    const form = new FormData();
    form.append('file', file);
    form.append('mapping', JSON.stringify(mapping));
    return wrap(() => api.post(`${base(t, id)}/contacts/import`, form, multipart));
  },

  // ── Audience ──
  /** Followers / members + contacts the page can reach. `take` (≤ 1000) lists the whole pool for PICK PEOPLE; rows carry onPortal · isMember · groupIds. */
  searchPeople: (t, id, q, take) => wrap(() => api.get(`${base(t, id)}/people`, { params: take ? { q, take } : { q } })),
  /** { count, noAddress, optedOut, cap } for { channel, audienceKind, audience }. */
  countAudience: (t, id, payload) => wrap(() => api.post(`${base(t, id)}/broadcasts/count`, payload)),

  // ── Broadcasts — the same client shape the admin newsletter builder uses ──
  listBroadcasts: (t, id) => wrap(() => api.get(`${base(t, id)}/broadcasts`)),
  /** A builder client bound to one page: get / save / remove / sendTest / send / schedule / unschedule / duplicate / uploadImage. */
  broadcastClient(t, id) {
    const b = base(t, id);
    return {
      get: (bid) => wrap(() => api.get(`${b}/broadcasts/${bid}`)),
      save: (payload) => wrap(() => api.post(`${b}/broadcasts`, payload)),
      remove: (bid) => wrap(() => api.delete(`${b}/broadcasts/${bid}`)),
      sendTest: (bid) => wrap(() => api.post(`${b}/broadcasts/${bid}/test`)),
      send: (bid) => wrap(() => api.post(`${b}/broadcasts/${bid}/send`)),
      schedule: (bid, iso) => wrap(() => api.post(`${b}/broadcasts/${bid}/schedule`, { scheduledFor: iso })),
      unschedule: (bid) => wrap(() => api.post(`${b}/broadcasts/${bid}/unschedule`)),
      duplicate: (bid) => wrap(() => api.post(`${b}/broadcasts/${bid}/duplicate`)),
      uploadImage: (file) => {
        const form = new FormData();
        form.append('file', file);
        return wrap(() => api.post(`${b}/broadcasts/image`, form, multipart));
      },
      // Email Studio — the page's schemes, palette, logo, templates and events.
      listSchemes: () => wrap(() => api.get(`${b}/email/schemes`)),
      saveScheme: (scheme) => wrap(() => (scheme.id ? api.put(`${b}/email/schemes/${scheme.id}`, scheme) : api.post(`${b}/email/schemes`, scheme))),
      deleteScheme: (sid) => wrap(() => api.delete(`${b}/email/schemes/${sid}`)),
      getPalette: () => wrap(() => api.get(`${b}/email/palette`)),
      replaceLogo: (file) => {
        const form = new FormData();
        form.append('file', file);
        return wrap(() => api.post(`${b}/email/logo`, form, multipart));
      },
      listTemplates: () => wrap(() => api.get(`${b}/email/templates`)),
      saveTemplate: (tpl) => wrap(() => api.post(`${b}/email/templates`, tpl)),
      deleteTemplate: (tid) => wrap(() => api.delete(`${b}/email/templates/${tid}`)),
      listEvents: (q) => wrap(() => api.get(`${b}/email/events`, { params: q ? { q } : {} })),
      /** { from, replyTo, defaultReplyTo } — the page's sending address and where replies go. */
      getEmailSettings: () => wrap(() => api.get(`${b}/email/settings`)),
      saveEmailSettings: (payload) => wrap(() => api.put(`${b}/email/settings`, payload)),
      /** USE YOUR OWN DOMAIN: { fromAddress } → { domain, fromAddress, status, records: [{ type, name, value, ok }] }. */
      connectDomain: (fromAddress) => wrap(() => api.post(`${b}/email/domain`, { fromAddress })),
      checkDomain: () => wrap(() => api.post(`${b}/email/domain/check`)),
      disconnectDomain: () => wrap(() => api.delete(`${b}/email/domain`)),
      /** { zone, provider, providerLink, records: [{ type, host, name, value, ok, optional }], applyUrl, cloudflare, needsDmarc } */
      domainOptions: (returnPath) => wrap(() => api.get(`${b}/email/domain/options`, { params: returnPath ? { returnPath } : {} })),
      /** Adds the records with the owner's Cloudflare token — used once, never stored. */
      domainViaCloudflare: (token) => wrap(() => api.post(`${b}/email/domain/cloudflare`, { token })),
    };
  },

  // ── Public: /join/{code} ──
  describeLink: (code) => wrap(() => api.get(`/contact-links/${encodeURIComponent(code)}`)),
  /** { email?, phone?, phoneCountry?, name? } → { outcome, hasAccount, name, publicPath } */
  joinLink: (code, payload) => wrap(() => api.post(`/contact-links/${encodeURIComponent(code)}/join`, payload)),

  // ── Pro follow ──
  followPro: (slug) => wrap(() => api.post(`/pro/${slug}/follow`)),
  unfollowPro: (slug) => wrap(() => api.delete(`/pro/${slug}/follow`)),
};

export default audienceService;
